"use client";
import { memo, type ReactElement } from "react";
import { ColorType } from "@/theme/colors";
import { FontWeight } from "@/theme/typography";
import { getProviderIconPath } from "@/variety/llmProviders";
import { Cimage } from "../Cimage";
import { Ctext } from "../Ctext";
import { CTEAMCARD_ICONS_CONTAINER_STYLE, CTEAMCARD_IMAGE_DECODING_ASYNC, CTEAMCARD_IMAGE_LOADING_LAZY, CTEAMCARD_MAX_PROVIDER_ICONS, getCteamcardIconWrapperStyle, getCteamcardProviderIconSize, } from "./Cteamcard.styles";
const CTEAMCARD_PROVIDER_OVERFLOW_NAME = "overflow";
interface CteamcardProviderIconsProps {
    vendors: string[];
    isMobile: boolean;
    priority: boolean;
    useBackdropFilter: boolean;
}
interface ProviderIconItem {
    key: string;
    zIndex: number;
    content: ReactElement;
}
interface BuildProviderIconItemsInput {
    vendors: string[];
    iconSize: number;
    priority: boolean;
}
interface BuildOverflowIconItemInput {
    overflowCount: number;
    totalItems: number;
}
interface BuildProviderIconItemInput {
    provider: string;
    index: number;
    iconSize: number;
    totalItems: number;
    hasOverflow: boolean;
    priority: boolean;
}
function buildOverflowIconItem({ overflowCount, totalItems, }: BuildOverflowIconItemInput): ProviderIconItem {
    return {
        key: CTEAMCARD_PROVIDER_OVERFLOW_NAME,
        zIndex: totalItems,
        content: (<Ctext size={14} weight={FontWeight.Bold} color={ColorType.White} opacity={0.8}>
                +{overflowCount}
            </Ctext>),
    };
}
function buildProviderIconItem({ provider, index, iconSize, totalItems, hasOverflow, priority, }: BuildProviderIconItemInput): ProviderIconItem {
    const iconPath = getProviderIconPath(provider);
    const zIndexOffset = hasOverflow ? index + 1 : index;
    return {
        key: `${provider}-${index}`,
        zIndex: totalItems - 1 - zIndexOffset,
        content: (<Cimage src={iconPath} alt={provider} width={iconSize} height={iconSize} static={true} priority={priority} loading={CTEAMCARD_IMAGE_LOADING_LAZY} decoding={CTEAMCARD_IMAGE_DECODING_ASYNC} objectFit="contain" style={{
                width: "100%",
                height: "100%",
            }}/>),
    };
}
function buildProviderIconItems({ vendors, iconSize, priority, }: BuildProviderIconItemsInput): ProviderIconItem[] {
    const visibleVendors = vendors.slice(0, CTEAMCARD_MAX_PROVIDER_ICONS);
    const overflowCount = Math.max(0, vendors.length - CTEAMCARD_MAX_PROVIDER_ICONS);
    const hasOverflow = overflowCount > 0;
    const totalItems = visibleVendors.length + Number(hasOverflow);
    const overflowItems = hasOverflow
        ? [buildOverflowIconItem({ overflowCount, totalItems })]
        : [];
    const providerItems = visibleVendors.map((provider, index) => (buildProviderIconItem({
        provider,
        index,
        iconSize,
        totalItems,
        hasOverflow,
        priority,
    })));
    return [...overflowItems, ...providerItems];
}
export const CteamcardProviderIcons = memo(function CteamcardProviderIcons({ vendors, isMobile, priority, useBackdropFilter, }: CteamcardProviderIconsProps): ReactElement {
    const iconSize = getCteamcardProviderIconSize({ isMobile });
    const providerIconItems = buildProviderIconItems({
        vendors,
        iconSize,
        priority,
    });
    return <div style={CTEAMCARD_ICONS_CONTAINER_STYLE}>
            {providerIconItems.map(({ key, zIndex, content, }) => (<div key={key} style={getCteamcardIconWrapperStyle({
                iconSize,
                useBackdropFilter,
                zIndex,
            })}>
                    {content}
                </div>))}
        </div>
        ;
});
